import { Injectable, OnDestroy } from "@angular/core";
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  finalize,
  map,
  Observable,
  of,
  Subject,
  take,
  takeUntil,
  withLatestFrom,
} from "rxjs";
import {
  AppLoadError,
  IAddress,
  ICountry,
  ILanguage,
  IPersonalInfo,
  IPersonalInfoReview,
  IPhone,
  PersonalInfo,
} from "../shared/models";
import { AuthService } from "../shared/auth/auth.service";
import { HttpErrorResponse } from "@angular/common/http";
import { commonEnv } from "src/environments/environment.common";
import { PersonalInfoService } from "../shared/services/api/personal-info.service";
import { SharedStoreService } from "./shared-store.service";
import { ApplicantInfoStoreService } from "./applicant-info-store.service";
import { getSafe, isAnyPropEmpty } from "../utils";
import moment from "moment";
import { CountryListStoreService } from "./country-list-store.service";
import { LanguageListStoreService } from "./language-list-store.service";
import { GtmService } from "../shared/services/gtm.service";
import { AppStateService } from "../shared/services/app-state.service";

@Injectable({
  providedIn: "root",
})
export class PersonalInfoStoreService implements OnDestroy {
  private readonly _personalInfo = new BehaviorSubject<PersonalInfo | null>(
    null
  );
  private readonly _loading = new BehaviorSubject<boolean>(false);
  private readonly _error = new BehaviorSubject<AppLoadError | null>(null);
  private readonly _saving = new BehaviorSubject<boolean>(false);

  private destroyed$ = new Subject<void>();

  // Exposed Observables
  personalInfo$: Observable<PersonalInfo | null> =
    this._personalInfo.asObservable();
  loading$: Observable<boolean> = this._loading.asObservable();
  error$: Observable<AppLoadError | null> = this._error.asObservable();
  saving$: Observable<boolean> = this._saving.asObservable();

  get personalInfo() {
    return this._personalInfo.getValue();
  }

  constructor(
    private personalInfoService: PersonalInfoService,
    private authService: AuthService,
    private sharedStore: SharedStoreService,
    private applicantInfoStore: ApplicantInfoStoreService,
    private countryListStore: CountryListStoreService,
    private languageListStore: LanguageListStoreService,
    private appState: AppStateService,
    private gtmService: GtmService
  ) {}

  selectPersonalInfo(): Observable<PersonalInfo | null> {
    return this.personalInfo$.pipe(map((personalInfo) => personalInfo));
  }

  selectLoading(): Observable<boolean> {
    return this.loading$.pipe(map((loading) => loading));
  }

  selectError(): Observable<AppLoadError | null> {
    return this.error$.pipe(map((error) => error));
  }

  selectSaving(): Observable<boolean> {
    // Provide a method to check saving state
    return this.saving$.pipe(map((saving) => saving));
  }

  selectPersonalInfoReview() {
    return combineLatest([
      this.loadPersonalInfo(this.applicantInfoStore.applicantInfo.appId),
      this.countryListStore.loadCountryList(),
      this.languageListStore.loadLanguageList(),
    ]).pipe(
      takeUntil(this.destroyed$),
      map(([personalInfo, countryList, languageList]) => {
        if (personalInfo && countryList.length > 0 && languageList.length > 0) {
          const data = personalInfo;
          return this.constructPersonalInfoReview(
            data,
            countryList,
            languageList
          );
        }

        return of(null);
      })
    );
  }

  /**
   * The `loadPersonalInfo` function retrieves personal information, updates the store, and handles
   * errors.
   * @GET
   * @param {string | null} [applicationId=null] - The `applicationId` parameter in the
   * `loadPersonalInfo` function is a string that represents the ID of the application for which the
   * personal information is being loaded. It is optional and can be `null` if not provided.
   * @returns The `loadPersonalInfo` method returns an Observable of type `PersonalInfo | null`.
   */
  loadPersonalInfo(
    applicationId: string | null = null
  ): Observable<PersonalInfo | null> {
    this._loading.next(true);

    return this.personalInfoService.getPersonalInfo().pipe(
      withLatestFrom(of(applicationId)),
      map(([personalInfo, appId]) => {
        if (!personalInfo || Object.keys(personalInfo).length === 0) {
          personalInfo = new PersonalInfo(); // Create empty object for new applicants
        }
        if (appId) {
          personalInfo.appId = appId; // Assign application ID if available
        }
        this._personalInfo.next(personalInfo); // Update store
        this._error.next(null); // Clear errors
        return personalInfo; // Return updated personal info
      }),
      catchError((err: HttpErrorResponse) => {
        const errorMessage =
          err.error?.message || "Failed to load personal information.";
        this._error.next(errorMessage);
        return of(null); // Emit null in case of error
      }),
      finalize(() => this._loading.next(false)) // Finalize loading state
    );
  }

  /**
   * Submits personal information and updates the store.
   * @param payload - The personal information payload to submit.
   */
  submitPersonalInfo(payload: PersonalInfo) {
    this._saving.next(true); // Start saving
    this._loading.next(true); // Start loading

    return this.personalInfoService.postPersonalInfo(payload).pipe(
      map((response: any) => {
        const appId = response?.appId || null;

        if (
          this.authService.getUserInfo()?.tfp === commonEnv.agentSignInPolicy
        ) {
          this.applicantInfoStore
            .loadApplicantInfo(appId)
            .pipe(takeUntil(this.destroyed$))
            .subscribe();
        } else {
          this.applicantInfoStore
            .loadApplicantInfo()
            .pipe(takeUntil(this.destroyed$))
            .subscribe();
        }

        this.appState.personalInfo = this.personalInfo;

        // push the tag to the google tag maanager
        this.gtmService.pushTag({
          event: "international_application_progress",
          step_number: "1",
          step_name: "personal-info",
        });

        // redirects to the next step
        this.sharedStore.triggerNavigationAfterSubmit("PERSONAL_INFO");
        this._error.next(null); // Clear errors
        return response;
      }),
      catchError((err: HttpErrorResponse) => {
        const errorMessage =
          err.error?.message || "Failed to submit personal information.";
        this._error.next(errorMessage);
        console.log("ERROR : " + errorMessage);
       // Prevent breaking the stream
       return of(errorMessage);
        //return of(null);
      }),
      takeUntil(this.destroyed$),
      finalize(() => {
        this._saving.next(false); // End saving
        this._loading.next(false); // End loading
      })
    );
  }

  constructAddress = (address: IAddress) => {
    if (!address) return null;
    const { stAddress, stAddress2, city, province, country, zipCode } = address;

    return [stAddress, stAddress2, city, province, country, zipCode]
      .filter((item) => item)
      .join(", ");
  };

  constructPhoneNumber = (phone: IPhone) => {
    if (!phone || isAnyPropEmpty(phone)) return `Not provided`;

    const { countryCode, number } = phone;

    return `(${countryCode}) ${number}`;
  };

  constructGenderName = (gender: string) => {
    const genders = {
      m: "Male",
      f: "Female",
      x: "Other gender",
      u: "Unknown",
      default: "Unknown",
    };
    return genders[gender] || genders["default"];
  };

  constructStatus = (status: string) => {
    const statuses = {
      1: "I do not require a study permit according to IRCC",
      2: "I have not applied for my study permit",
      3: "I applied for my study permit",
      4: "I received my Letter of Introduction (upload your letter)",
      5: "I received my Study Permit (upload your permit)",
    };
    return statuses[status] || statuses["default"];
  };

  /*
Shorthand text for review page
 */
  constructPal = (status: string) => {
    const statuses = {
      1: "Study Permit Holder/Applicant",
      2: "Work Permit Holder",
      3: "Temporary Resident: Visiting/Exchange Student at DLI",
      4: "Temporary Resident: Completed Prerequisite Program at DLI",
      5: "Temporary Resident Permit Valid for at Least 6 Months",
      6: "Protected Person",
      7: "Eligible for Permanent Residence Confirmed by IRCC",
      8: "Family Member of Foreign National in Canada",
      9: "Unused Valid Provincial Attestation Letter Holder",
      10: "PAL Required",
    };
    return statuses[status] || statuses["default"];
  };

  // clear the UCI number if the user selects the first two options since there is no UCI number
  constructUCI = (status: string, uci_number: string) => {
    if (status == "1" || status == "2") {
      return null;
    }
    return uci_number;
  };

  constructDate = (date: Date) => {
    if (date != null) {
      return moment.utc(date).format("LL");
    } else {
      return null;
    }
  };

  constructPersonalInfoReview = (
    info: IPersonalInfo,
    countryList: ICountry[],
    languageList: ILanguage[]
  ): IPersonalInfoReview => {
    const language = getSafe(
      () => languageList.find((lang) => lang.code === info.primaryLang).name
    );
    const countryOA = getSafe(
      () => countryList.find((country) => country.code === info.countryOA).name
    );
    const countryOB = getSafe(
      () => countryList.find((country) => country.code === info.countryOB).name
    );
    const countryOC = getSafe(
      () => countryList.find((country) => country.code === info.countryOC).name
    );

    const primaryAddressCountry = countryList.find(
      (country) => country.code === info.permanentAddress.country
    );
    const primaryAddressProvince =
      info.permanentAddress.province !== "none" && primaryAddressCountry
        ? primaryAddressCountry.provinceList.find(
            (province) => province.code === info.permanentAddress.province
          ).name
        : "none";
    const primaryAddress: IAddress = {
      ...info.permanentAddress,
      country: primaryAddressCountry?.name,
      province: primaryAddressProvince as string,
    };

    let secondaryAddress: IAddress = null;

    if (!info.currAddrSameAsPerm) {
      const secondaryAddressCountry = countryList.find(
        (country) => country.code === info.mailingAddress.country
      );

      const secondaryAddressProvince =
        info.permanentAddress.province !== "none" && secondaryAddressCountry
          ? secondaryAddressCountry.provinceList.find(
              (province) => province.code === info.mailingAddress.province
            ).name
          : "none";

      secondaryAddress = {
        ...info.mailingAddress,
        country: secondaryAddressCountry.name,
        province: secondaryAddressProvince as string,
      };
    } else {
      secondaryAddress = {
        ...primaryAddress,
      };
    }

    var fName = info.firstName;
    if (info.firstName == null) {
      fName = "";
    }

    return {
      fullName: `${info.title}. ${fName} ${info.otherNames} ${info.surName}`,
      gender: this.constructGenderName(info.gender),
      dob: this.constructDate(info.dob),
      countryOfCitizenship: countryOC,
      countryOfApplication: countryOA,
      countryOfBirth: countryOB,
      primaryLanguage: language,
      permanentAddress: this.constructAddress(primaryAddress),
      phone: this.constructPhoneNumber(info.permanentAddress.phone),
      cell: this.constructPhoneNumber(info.permanentAddress.cell),
      email: info.email,
      currentAddress: this.constructAddress(secondaryAddress),
      status: this.constructStatus(info.status),
      uci_number: this.constructUCI(info.status, info.uci_number),
      issue_date: this.constructDate(info.issue_date),
      expiry_date: this.constructDate(info.expiry_date),
      status_pal: this.constructPal(info.status_pal),
      status_value: info.status, // this data is junk, keep it last from showing on the screen
    };
  };

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
